/**
 * Linear congruential generator (LCG) PRNG with seed support.
 * Deterministic: same seed → same sequence.
 */
export class SeededRandom {
  constructor(seed) {
    this._seed = this._hashSeed(seed);
  }

  _hashSeed(seed) {
    if (typeof seed === 'number') {
      // Avalanche mix: ensures adjacent seeds (1, 2, 3) produce very different states
      let h = (seed * 2654435761) >>> 0;  // Knuth multiplicative hash
      h = ((h >>> 16) ^ h) * 0x45d9f3b >>> 0;
      h = ((h >>> 16) ^ h) >>> 0;
      return (h & 0x7fffffff) || 1;
    }
    // Hash string seeds
    let hash = 0;
    const str = String(seed);
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
    }
    return Math.abs(hash) || 1;
  }

  /** Returns float in [0, 1). */
  next() {
    // LCG constants from Numerical Recipes
    this._seed = (this._seed * 1664525 + 1013904223) & 0x7fffffff;
    return this._seed / 0x7fffffff;
  }

  /** Returns integer in [min, max] inclusive. */
  nextInt(min, max) {
    return Math.floor(this.next() * (max - min + 1)) + min;
  }

  /** Returns float in [min, max). */
  nextFloat(min, max) {
    return this.next() * (max - min) + min;
  }

  /** Returns true with given probability (0-1). */
  chance(probability) {
    return this.next() < probability;
  }

  /** Pick random element from array. */
  pick(array) {
    if (!array || array.length === 0) return undefined;
    return array[this.nextInt(0, array.length - 1)];
  }

  /** Shuffle array in-place (Fisher-Yates). */
  shuffle(array) {
    for (let i = array.length - 1; i > 0; i--) {
      const j = this.nextInt(0, i);
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }

  /** Pick N unique elements from array. */
  sample(array, count) {
    const copy = [...array];
    this.shuffle(copy);
    return copy.slice(0, Math.min(count, copy.length));
  }

  /** Weighted random pick. items = [{ weight, value }] */
  weightedPick(items) {
    const total = items.reduce((sum, item) => sum + (item.weight || 1), 0);
    let r = this.next() * total;
    for (const item of items) {
      r -= item.weight || 1;
      if (r <= 0) return item.value !== undefined ? item.value : item;
    }
    return items[items.length - 1].value !== undefined ? items[items.length - 1].value : items[items.length - 1];
  }
}
